import { Link } from "react-router-dom";
import { applicationHistoryPath, useApi } from "../api/client";
import type { ApplicationHistory as History } from "../api/types";
import { Empty, ErrorState, Loading } from "./States";
import { EM_DASH, fmtDateTime, humanise } from "../lib/format";

/**
 * Which documents this application has run, and how many times each.
 *
 * The application page answers "where does this case stand", and it could only
 * ever answer it for the current state. A document re-run three times before it
 * extracted cleanly looked identical to one that worked first time — which
 * matters when the question is whether the file is ready, and more when it is
 * why the file took so long.
 */
export function ApplicationHistory({ id }: { id: string }) {
  const { data, error, loading, reload } = useApi<History>(
    applicationHistoryPath(id),
  );

  if (loading)
    return (
      <div className="card">
        <Loading what="document history" />
      </div>
    );
  if (error)
    return (
      <div className="card">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return null;

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">Documents run</span>
        <span className="muted">
          {data.document_count} document(s) · {data.total_attempts} extraction
          attempt(s)
          {data.verified_documents
            ? ` · ${data.verified_documents} verified`
            : null}
        </span>
      </div>
      <div className="card-body">
        {/* The fact the current state hides: this application cost more runs
            than it has documents. */}
        {data.rerun_documents > 0 ? (
          <div className="notice notice-warn">
            {data.rerun_documents} of {data.document_count} document(s) were run
            more than once — {data.total_attempts} attempts for{" "}
            {data.document_count} documents.
          </div>
        ) : null}

        {data.documents.length === 0 ? (
          <Empty
            title="No documents on this application"
            hint="Documents submitted with this application_id will appear here."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Document</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">Runs</th>
                  <th scope="col" className="num">Agent</th>
                  <th scope="col" className="num">Required</th>
                  <th scope="col">Format</th>
                  <th scope="col">First submitted</th>
                </tr>
              </thead>
              <tbody>
                {data.documents.map((d) => (
                  <tr key={d.document_id}>
                    <td>
                      <Link to={`/documents/${d.document_id}`}>
                        {d.original_filename ?? d.document_id.slice(0, 8)}
                      </Link>
                      {d.verified_fields ? (
                        <div className="muted" style={{ fontSize: 11 }}>
                          {d.verified_fields} field(s) verified
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {humanise(d.document_type ?? "unknown")}
                    </td>
                    <td className="nowrap">
                      <span
                        className={`pill ${
                          d.status === "failed" ? "pill-critical" : "pill-good"
                        }`}
                      >
                        {humanise(d.status ?? "unknown")}
                      </span>
                    </td>
                    <td className="num">
                      {d.attempts > 1 ? (
                        <Link
                          to={`/documents/${d.document_id}?tab=history`}
                          title={`${d.attempts} attempts, ${d.failed_attempts} failed`}
                        >
                          {d.attempts}
                        </Link>
                      ) : (
                        <span className="muted">{d.attempts}</span>
                      )}
                    </td>
                    <td className="num muted">
                      {d.agent_confidence === null
                        ? EM_DASH
                        : `${d.agent_confidence}%`}
                    </td>
                    <td className="num">
                      {d.required_score === null ? (
                        <span className="muted">{EM_DASH}</span>
                      ) : (
                        <span
                          className={
                            d.required_score === 100
                              ? "req-ok"
                              : d.required_score >= 60
                                ? "req-warn"
                                : "req-bad"
                          }
                        >
                          {d.required_score}%
                        </span>
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: 12.5 }}>
                      {d.format_name ?? EM_DASH}
                    </td>
                    <td className="nowrap muted" style={{ fontSize: 12.5 }}>
                      {fmtDateTime(d.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
